/**
 * Dashboards Service — Oportunidades Comerciales v2
 *
 * Reemplaza el flujo monolítico de opportunitiesService.js por un modelo de
 * dashboards + widgets. Cada widget se regenera por separado contra Bedrock.
 */

const API_URL = process.env.REACT_APP_STRANDS_API_URL || 'http://localhost:5001';

const qs = (params) => {
  const usp = new URLSearchParams();
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') usp.set(k, v);
  });
  const s = usp.toString();
  return s ? `?${s}` : '';
};

async function request(path, { method = 'GET', body } = {}) {
  const opts = {
    method,
    headers: { 'Content-Type': 'application/json' },
  };
  if (body !== undefined) opts.body = JSON.stringify(body);

  const res = await fetch(`${API_URL}${path}`, opts);
  let data = null;
  try { data = await res.json(); } catch { /* respuesta vacía o no-JSON */ }

  if (!res.ok) {
    const msg = (data && (data.error || data.message)) || `HTTP ${res.status}`;
    const err = new Error(msg);
    err.status = res.status;
    err.payload = data;
    throw err;
  }
  return data;
}

// ---------------------------------------------------------------------------
// Meta
// ---------------------------------------------------------------------------

export const getDashboardMeta = (username = '') =>
  request(`/api/dashboards/meta${qs({ username })}`);

/**
 * Pide a la IA que proponga la configuración completa de un widget a partir
 * de una descripción en lenguaje natural. No crea nada, solo devuelve la
 * sugerencia para que el frontend abra el modal pre-llenado.
 */
export const suggestWidget = (username, description) =>
  request('/api/dashboards/suggest-widget', {
    method: 'POST',
    body: { username, description },
  });

// ---------------------------------------------------------------------------
// Dashboards
// ---------------------------------------------------------------------------

export const listDashboards = (username) =>
  request(`/api/dashboards${qs({ username })}`);

export const getDefaultDashboard = (username) =>
  request(`/api/dashboards/default${qs({ username })}`);

export const getDashboard = (dashboardId, username) =>
  request(`/api/dashboards/${dashboardId}${qs({ username })}`);

export const createDashboard = ({ username, name, with_defaults = false, is_default = false }) =>
  request('/api/dashboards', {
    method: 'POST',
    body: { username, name, with_defaults, is_default },
  });

export const updateDashboard = (dashboardId, { username, name, is_default, columns }) =>
  request(`/api/dashboards/${dashboardId}`, {
    method: 'PATCH',
    body: { username, name, is_default, columns },
  });

export const deleteDashboard = (dashboardId, username) =>
  request(`/api/dashboards/${dashboardId}${qs({ username })}`, { method: 'DELETE' });

export const refreshDashboard = (dashboardId, username, { mode = 'fast' } = {}) =>
  request(`/api/dashboards/${dashboardId}/refresh`, {
    method: 'POST',
    body: { username, mode },
  });

export const reorderWidgets = (dashboardId, username, widgetIds) =>
  request(`/api/dashboards/${dashboardId}/reorder`, {
    method: 'POST',
    body: { username, widget_ids: widgetIds },
  });

// ---------------------------------------------------------------------------
// Widgets
// ---------------------------------------------------------------------------

export const createWidget = (dashboardId, { username, widget_type, dataset, title, prompt, chart_type, size, position, generate = false }) =>
  request(`/api/dashboards/${dashboardId}/widgets`, {
    method: 'POST',
    body: { username, widget_type, dataset, title, prompt, chart_type, size, position, generate },
  });

export const updateWidget = (dashboardId, widgetId, payload) =>
  request(`/api/dashboards/${dashboardId}/widgets/${widgetId}`, {
    method: 'PATCH',
    body: payload,
  });

export const deleteWidget = (dashboardId, widgetId, username) =>
  request(`/api/dashboards/${dashboardId}/widgets/${widgetId}${qs({ username })}`, {
    method: 'DELETE',
  });

export const refreshWidget = (dashboardId, widgetId, username, { mode = 'fast' } = {}) =>
  request(`/api/dashboards/${dashboardId}/widgets/${widgetId}/refresh`, {
    method: 'POST',
    body: { username, mode },
  });

// ---------------------------------------------------------------------------
// Helpers de formato (rehúsa opportunitiesService.js cuando aplica)
// ---------------------------------------------------------------------------

export const formatReportDate = (isoDate) => {
  if (!isoDate) return 'Nunca';
  return new Date(isoDate).toLocaleDateString('es-MX', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

export const formatPercentage = (value, decimals = 1) => {
  if (value === null || value === undefined || isNaN(value)) return 'N/A';
  const num = Number(value);
  const sign = num > 0 ? '+' : '';
  return `${sign}${num.toFixed(decimals)}%`;
};

export const formatNumber = (value) => {
  if (value === null || value === undefined || isNaN(value)) return 'N/A';
  return new Intl.NumberFormat('es-MX').format(Number(value));
};

export const formatCurrency = (value, currency = 'MXN') => {
  if (value === null || value === undefined || isNaN(value)) return 'N/A';
  return new Intl.NumberFormat('es-MX', {
    style: 'currency', currency, maximumFractionDigits: 2,
  }).format(Number(value));
};

/**
 * Aplica el formato declarado en data.format (number|percent|currency) al value.
 */
export const formatWidgetValue = (value, format) => {
  if (value === null || value === undefined || isNaN(Number(value))) return 'N/A';
  switch ((format || 'number').toLowerCase()) {
    case 'percent':  return formatPercentage(value);
    case 'currency': return formatCurrency(value);
    default:         return formatNumber(value);
  }
};

const dashboardsService = {
  getDashboardMeta,
  suggestWidget,
  listDashboards,
  getDefaultDashboard,
  getDashboard,
  createDashboard,
  updateDashboard,
  deleteDashboard,
  refreshDashboard,
  reorderWidgets,
  createWidget,
  updateWidget,
  deleteWidget,
  refreshWidget,
  formatReportDate,
  formatPercentage,
  formatNumber,
  formatCurrency,
  formatWidgetValue,
};

export default dashboardsService;
